import React, { FC, useEffect, useState } from 'react';
import { loadStripe, Stripe, Appearance } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import useClientSecret from '../../../hooks/api/payment/stripe/useClientSecret';
import { FormModel } from '../../../models/form/form';
import FormSteps from './steps/FormSteps';
import { OrganizationModel } from '../../../models/organization/organization';
import LoadingSpinner from '../../layout/loadingSpinner/LoadingSpinner';

interface Props {
  organization: OrganizationModel;
  form: FormModel;
  showShareModal?: boolean;
}

const appearance: Appearance = {
  theme: 'none',

  variables: {
    fontFamily: 'Poppins, sans-serif',
    colorPrimary: '#2f438d',
    colorBackground: '#F3F3F3',
    colorText: '#2f438d',
    borderRadius: '12px',
  },

  rules: {
    '.Label': {
      fontSize: '19px',
      fontWeight: '500',
      color: '#16164c',
      marginBottom: '0.5rem',
      lineHeight: '28.5px',
    },
    '.Input': {
      border: '2px solid #F3F3F3',
      fontWeight: '400',
      padding: '8px 14px',
      fontSize: '19px',
      lineHeight: '28.5px',
      color: '#495057',
      transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
    },
    '.Input::placeholder': {
      color: '#B1B1C1',
      fontWeight: '400',
    },
    '.Input:focus': {
      borderColor: '$submenu-color',
      outline: 'none',
    },
  },
};

const Form: FC<Props> = ({ form, organization, showShareModal }) => {
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const { clientSecret, setAccountId, setOrganizationId: setStripeOrganizationId, isLoading } = useClientSecret();

  useEffect(() => {
    if (form && form.paymentEnabled) {
      setAccountId(form.paymentAccount);
      setStripeOrganizationId(form.organizationId);
      setStripePromise(
        loadStripe(window.configs.stripePublishableKey, {
          stripeAccount: form.paymentAccount,
        }),
      );
    }
  }, [form]);
  if (!form || !organization) {
    return null;
  }

  return (
    <React.Fragment>
      <div>
        {isLoading && <LoadingSpinner />}
        {!isLoading && form.paymentEnabled && clientSecret && (
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret: clientSecret.clientSecret,
              appearance,
              fonts: [{ cssSrc: 'https://fonts.googleapis.com/css?family=Poppins:300,400,500,600,700&display=swap' }],
            }}
          >
            <FormSteps
              organization={organization}
              form={form}
              showLimitReached={true}
              setupIntentId={clientSecret.setupIntentId}
              clientSecret={clientSecret.clientSecret}
              showShareModal={showShareModal}
            />
          </Elements>
        )}
        {!isLoading && !form.paymentEnabled && (
          <FormSteps
            organization={organization}
            form={form}
            showLimitReached={true}
            showShareModal={showShareModal}
            clientSecret=""
          />
        )}
      </div>
    </React.Fragment>
  );
};

export default Form;
